import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Send,
  Lock,
  User,
  Shield,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { SupportTicket, SupportMessage } from '../../types/database';

interface TicketChatModalProps {
  ticket: SupportTicket | null;
  isOpen: boolean;
  onClose: () => void;
  onTicketUpdated?: () => void;
}

export default function TicketChatModal({
  ticket,
  isOpen,
  onClose,
  onTicketUpdated,
}: TicketChatModalProps) {
  const { user, profile, isStaff } = useAuth();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<string>(ticket?.status || 'Open');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ticket) {
      setCurrentStatus(ticket.status);
      fetchMessages();
      subscribeToMessages();
    }
    return () => {
      supabase.channel(`ticket-${ticket?.id}`).unsubscribe();
    };
  }, [ticket?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    if (!ticket) return;
    setLoading(true);
    try {
      let query = supabase
        .from('support_messages')
        .select('*, sender:profiles(*)')
        .eq('ticket_id', ticket.id)
        .order('created_at', { ascending: true });

      // If not staff, enforce hiding internal notes
      if (!isStaff) {
        query = query.eq('is_internal_note', false);
      }

      const { data, error } = await query;
      if (!error && data) {
        setMessages(data as SupportMessage[]);
      }
    } catch (err) {
      console.error('Error fetching ticket messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToMessages = () => {
    if (!ticket) return;

    const channel = supabase
      .channel(`ticket-${ticket.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `ticket_id=eq.${ticket.id}`,
        },
        async (payload) => {
          const newMsg = payload.new as SupportMessage;
          // If non-staff and it's an internal note, don't show
          if (!isStaff && newMsg.is_internal_note) return;

          // Fetch sender profile
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', newMsg.sender_id)
            .single();

          setMessages((prev) => [
            ...prev,
            { ...newMsg, sender: senderProfile || undefined },
          ]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !ticket || !user) return;

    setSending(true);
    try {
      const { data, error } = await supabase
        .from('support_messages')
        .insert({
          ticket_id: ticket.id,
          sender_id: user.id,
          message: newMessage.trim(),
          is_internal_note: isStaff ? isInternalNote : false,
        })
        .select('*, sender:profiles(*)')
        .single();

      if (!error && data) {
        // Also update ticket updated_at and status if needed
        const newStatus = isStaff ? 'Waiting for Client' : 'Waiting for Staff';
        await supabase
          .from('support_tickets')
          .update({
            updated_at: new Date().toISOString(),
            status: ticket.status === 'Resolved' || ticket.status === 'Closed' ? ticket.status : newStatus,
          })
          .eq('id', ticket.id);

        setNewMessage('');
        setIsInternalNote(false);
        onTicketUpdated?.();
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!ticket) return;
    setUpdatingStatus(true);
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', ticket.id);

      if (!error) {
        setCurrentStatus(newStatus);
        onTicketUpdated?.();
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!isOpen || !ticket) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full h-[85vh] flex flex-col overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="p-5 bg-darkBlue-900 text-white flex items-center justify-between border-b border-darkBlue-800">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs font-bold text-clairBlue-300">
                {ticket.ticket_number}
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  currentStatus === 'Resolved' || currentStatus === 'Closed'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : currentStatus === 'Open'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                    : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                }`}
              >
                {currentStatus}
              </span>
              <span className="text-[10px] text-gray-400 bg-white/10 px-2 py-0.5 rounded-md">
                {ticket.priority} Priority
              </span>
            </div>
            <h3 className="font-bold text-base sm:text-lg leading-tight line-clamp-1">
              {ticket.subject}
            </h3>
          </div>

          <div className="flex items-center gap-3">
            {/* Status change dropdown for staff */}
            {isStaff && (
              <select
                value={currentStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={updatingStatus}
                className="bg-darkBlue-800 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-darkBlue-700 focus:outline-none focus:ring-1 focus:ring-clairBlue-400 cursor-pointer"
              >
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Waiting for Client">Waiting for Client</option>
                <option value="Waiting for Staff">Waiting for Staff</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
              </select>
            )}

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Original Issue Description Banner */}
        <div className="bg-clairBlue-50/70 p-4 border-b border-clairBlue-100 text-xs">
          <div className="flex items-center gap-1.5 font-bold text-darkBlue-900 mb-1">
            <FileText className="w-3.5 h-3.5 text-clairBlue-600" />
            Original Issue Description
            <span className="font-normal text-gray-500 ml-auto">
              Opened {new Date(ticket.created_at).toLocaleString()}
            </span>
          </div>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{ticket.description}</p>
        </div>

        {/* Message Thread Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-full text-xs text-gray-400">
              Loading conversation history...
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 py-12">
              <Clock className="w-8 h-8 mb-2 text-gray-300" />
              <p className="text-xs font-medium">No messages yet on this ticket.</p>
              <p className="text-[11px] text-gray-400 mt-1">Send a message below to begin.</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMe = msg.sender_id === user?.id;
              const isStaffMsg = !isMe && isStaff;

              if (msg.is_internal_note) {
                return (
                  <div
                    key={msg.id}
                    className="p-3 bg-amber-50/90 border border-amber-200/80 rounded-2xl text-xs space-y-1 shadow-sm"
                  >
                    <div className="flex items-center gap-1.5 text-amber-800 font-bold text-[11px]">
                      <Lock className="w-3 h-3 text-amber-600" />
                      Internal Staff Note — {msg.sender?.full_name || 'Staff Member'}
                      <span className="text-[10px] text-amber-600/70 font-normal ml-auto">
                        {new Date(msg.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-amber-900 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                );
              }

              return (
                <div
                  key={msg.id}
                  className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                >
                  <div className="flex items-center gap-1.5 text-[10px] text-gray-400 mb-1 px-1">
                    <span className="font-semibold text-gray-600">
                      {isMe ? 'You' : msg.sender?.full_name || 'Support Agent'}
                    </span>
                    <span>•</span>
                    <span>
                      {new Date(msg.created_at).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div
                    className={`max-w-[82%] px-4 py-3 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap shadow-sm ${
                      isMe
                        ? 'bg-darkBlue-900 text-white rounded-br-none'
                        : 'bg-white text-gray-800 border border-gray-200/70 rounded-bl-none'
                    }`}
                  >
                    {msg.message}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Bar */}
        <form onSubmit={handleSendMessage} className="p-3 sm:p-4 bg-white border-t border-gray-100">
          {/* Internal note checkbox for staff */}
          {isStaff && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <label className="flex items-center gap-1.5 text-xs text-amber-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isInternalNote}
                  onChange={(e) => setIsInternalNote(e.target.checked)}
                  className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                />
                <Lock className="w-3 h-3 text-amber-600" />
                <span className="font-semibold">Save as internal note</span>
                <span className="text-[10px] text-gray-400">(client will not see this)</span>
              </label>
            </div>
          )}

          <div className="flex items-end gap-2">
            <textarea
              rows={2}
              required
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder={
                isInternalNote
                  ? 'Type an internal team note...'
                  : 'Type your message or response... (Press Enter to send)'
              }
              className={`flex-1 px-4 py-2.5 rounded-xl border text-xs sm:text-sm focus:outline-none focus:ring-2 resize-none transition-colors ${
                isInternalNote
                  ? 'bg-amber-50/50 border-amber-200 focus:ring-amber-400'
                  : 'bg-gray-50 border-gray-200 focus:ring-clairBlue-500'
              }`}
            />

            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className={`h-10 px-4 rounded-xl text-white font-bold text-xs flex items-center gap-1.5 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${
                isInternalNote
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-darkBlue-900 hover:bg-darkBlue-800'
              }`}
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Send</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
